import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { trackRegisterStarted, trackRegisterCompleted } from '../../utils/analytics';
import lookToTheFuture from '../../assets/landing/look_to_the_future.webp';

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: '14px',
  lineHeight: '20px',
  fontFamily: "'Inter', system-ui, sans-serif",
  outline: 'none',
  boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  color: '#fff',
  marginBottom: '8px',
  fontFamily: "'Inter', system-ui, sans-serif",
};

export const FormSection: React.FC = () => {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [terms, setTerms] = useState(false);
  const invitationCode = t('landing_early_access_invitation_code');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError(t('passwords_not_match'));
      return;
    }
    if (password.length < 8) {
      setError(t('password_too_short'));
      return;
    }
    if (!terms) {
      setError(t('must_accept_terms'));
      return;
    }

    try {
      setLoading(true);
      await signup({ email, password, invitationCode });
      trackRegisterCompleted(invitationCode, locale);
      navigate('/desk');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signup_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cta"
      className="relative scroll-mt-24"
      style={{ backgroundImage: `url(${lookToTheFuture})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0" style={{ background: 'rgba(39,39,41,0.75)' }} aria-hidden="true" />

      <div className="relative z-10" style={{ maxWidth: '480px', margin: '0 auto', padding: '96px 32px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 300,
            fontSize: 'clamp(32px, 5vw, 52px)',
            lineHeight: 1.06,
            letterSpacing: '-0.02em',
            color: '#fff',
            margin: '0 0 12px',
          }}>
            {t('landing_cta_section_title')}
          </h2>
          <p style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: '14px',
            lineHeight: '20px',
            color: 'rgba(255,255,255,0.6)',
            margin: 0,
          }}>
            {t('landing_cta_section_subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Invitation code */}
          <div>
            <label style={LABEL_STYLE} htmlFor="cta-invitation">
              {t('invitation_code')}
            </label>
            <input
              id="cta-invitation"
              type="text"
              value={invitationCode}
              readOnly
              style={{
                ...INPUT_STYLE,
                opacity: 0.6,
                cursor: 'not-allowed',
                letterSpacing: '0.08em',
              }}
            />
          </div>

          {/* Email */}
          <div>
            <label style={LABEL_STYLE} htmlFor="cta-email">
              {t('landing_cta_email_placeholder')}
            </label>
            <input
              id="cta-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={INPUT_STYLE}
              placeholder="hello@example.com"
            />
          </div>

          {/* Password */}
          <div>
            <label style={LABEL_STYLE} htmlFor="cta-password">
              {t('landing_cta_password_label')}
            </label>
            <input
              id="cta-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={INPUT_STYLE}
              placeholder={t('landing_cta_password_placeholder')}
            />
          </div>

          {/* Confirm password */}
          <div>
            <label style={LABEL_STYLE} htmlFor="cta-confirm">
              {t('landing_cta_confirm_label')}
            </label>
            <input
              id="cta-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              style={INPUT_STYLE}
              placeholder={t('landing_cta_confirm_placeholder')}
            />
          </div>

          {/* Terms */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--scribane-green)', flexShrink: 0 }}
            />
            <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', system-ui, sans-serif" }}>
              {t('landing_cta_terms')}{' '}
              <Link to="/terms" style={{ color: 'var(--scribane-green)', textDecoration: 'underline' }}>
                {t('landing_cta_terms_link')}
              </Link>
            </span>
          </label>

          {/* Error */}
          {error && (
            <p style={{ fontSize: '14px', color: '#f87171', margin: 0 }}>{error}</p>
          )}

          {/* Submit */}
          <Button
            type="submit"
            variant="primary"
            className="w-full mt-2"
            disabled={loading}
            onClick={() => trackRegisterStarted(invitationCode, locale)}
          >
            {t('landing_cta_submit')}
            <ArrowRight size={16} />
          </Button>
        </form>
      </div>
    </section>
  );
};
